/* OnSumn — Flows Detail (T10): route-from-home step list + GETTING BACK
 * last-train block + the station's event cards. The route renderer is
 * extracted as <FlowsRoutePanel> (window-attached) so the mobile feed
 * inline-expansion (§5.4) and this desktop panel share ONE implementation
 * of §5.1 steps + §5.2 GETTING BACK + §5.3 chip prose + the D5 CTA. */

// Lens palette — spec §0.5, defined once, never re-derived.
const DETAIL_LENS = {
  dark:  { page: '#0C0F0F', panel: '#1A1C1C', ink: '#E9E7E2', muted: '#94897B', hairline: 'rgba(255,255,255,0.08)', reach1: '#FF6E32' },
  light: { page: '#F5F2EC', panel: '#FBF9F4', ink: '#191512', muted: '#6B5B4F', hairline: 'rgba(26,23,20,0.12)', reach1: '#BF3B00' },
};
// Reach ramp (§0.5) — doubles as the "~X min" text color; all ≥4.5:1.
const DETAIL_RAMP = {
  dark:  { reach1: '#FF6E32', reach2: '#FFB778', reach3: '#8FBF9F', reach4: '#A3BDCC', dim: '#8A8578' },
  light: { reach1: '#BF3B00', reach2: '#8E4E00', reach3: '#395542', reach4: '#3F5E70', dim: '#6B5B4F' },
};
const DETAIL_CAT_COLORS = (typeof window !== 'undefined' && window.CATEGORY_COLORS) || {
  'MUSIC': '#FF4F00', 'CARDS': '#7A99AC', 'ART': '#5A7A68',
  'FOOD': '#D57800', 'NIGHTLIFE': '#9F7BE5', 'COMMUNITY': '#395542',
};

// Chip colors (§5.3, amended P1): text + 10% wash + border rule, per tier
// colorKey. Surviving keys only — the graded tiers are gone (P1: state the
// fact, drop the judgment), and with them their color channel. 'fact' renders
// in plain ink: a clock is data, and data gets no verdict color.
function flowsChipColors(colorKey, dark) {
  if (colorKey === 'owl') {
    // The Owl's warm amber survives P1 unchanged (values were shared with a
    // retired key) — it marks a real all-night way home, not a grade.
    return dark ? { text: '#FFB778', wash: 'rgba(255,183,120,0.10)' } : { text: '#8E4E00', wash: 'rgba(142,78,0,0.10)' };
  }
  if (colorKey === 'fact') {
    // Lens ink (§0.5) — matches DETAIL_LENS.{dark,light}.ink; no wash.
    return dark ? { text: '#E9E7E2', wash: 'transparent' } : { text: '#191512', wash: 'transparent' };
  }
  // afterlast — dim text, no wash (§0.5)
  return dark ? { text: '#8A8578', wash: 'transparent' } : { text: '#6B5B4F', wash: 'transparent' };
}

// Non-interactive last-train stamp (§5.3). Glyph doubles as the non-color cue.
function FlowsLastTrainChip({ info, darkMode }) {
  if (!info) return null;
  const c = flowsChipColors(info.colorKey, darkMode);
  const hairline = darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(26,23,20,0.12)';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontFamily: 'var(--font-body)', fontSize: 10, fontWeight: 700,
      letterSpacing: '0.08em', textTransform: 'uppercase',
      color: c.text, background: c.wash,
      // Hairline border for the quiet stamps (afterlast + fact — P1: a factual
      // clock chip must not shout); currentColor only for the washed Owl chip.
      border: `1px solid ${(info.colorKey === 'afterlast' || info.colorKey === 'fact') ? hairline : 'currentColor'}`,
      padding: '3px 8px', borderRadius: 6, lineHeight: 1.3, whiteSpace: 'nowrap',
    }}>
      {info.glyph ? <span aria-hidden="true" style={{ fontSize: 11, lineHeight: 1 }}>{info.glyph}</span> : null}
      {info.text}
    </span>
  );
}

/* ── FlowsRoutePanel — shared route renderer (§5.1/§5.2/§5.3). ──
 * Computes the home→station steps + return-line last-train tier from
 * primitives, then renders the step list, GETTING BACK block, and CTA.
 * arrivalLabel/ctaUrl are display-specific (station name + coords on desktop;
 * event venue + coords in the mobile expansion). */
function FlowsRoutePanel({
  homeId, station, arrivalLabel, ctaUrl, eventStartMin, dayTypeOverride, todayYmdOverride,
  routePrev, stations, lines, lastTrain, darkMode, radius,
}) {
  const FF = window.FlowsFeedFormat;
  const lens = darkMode ? DETAIL_LENS.dark : DETAIL_LENS.light;
  const [ctaHov, setCtaHov] = React.useState(false);

  if (!station || !homeId || station.id === homeId) return null;

  const steps = window.buildRouteSteps
    ? window.buildRouteSteps(homeId, station.id, routePrev, stations, lines) : [];

  // Return route's first-leg line = the last ride step of the outbound route
  // (the line you board at the station heading home) — spec §5.2/§5.3.
  const rideSteps = steps.filter((s) => s.kind === 'ride');
  const returnLineId = rideSteps.length ? rideSteps[rideSteps.length - 1].lineId : null;

  const now = window.currentBand ? window.currentBand() : { dayType: 'weekday' };
  const today = FF ? FF.pacificToday() : null;
  // Deep-link representative override (§2.2): the LINKED event's own dayType +
  // holiday-ymd drive the last-train tier (Fri vs Sat vs Sun schedules differ);
  // today's values when no override is passed (the normal station-detail path).
  const tierDayType = dayTypeOverride || now.dayType;
  const tierYmd = todayYmdOverride || (today ? today.ymd : undefined);
  // B7 (§5.2 "always when home set"): compute the tier even with an unparseable
  // event start — lastTrainTier returns 'neutral' (factual last-departure clock,
  // no urgency grade) so GETTING BACK renders instead of vanishing. 'neutral'
  // carries no chip (the feed-row stamp stays start-gated); its line renders in
  // ink, not a tier color.
  const tier = (returnLineId && lastTrain && window.lastTrainTier)
    ? window.lastTrainTier(eventStartMin, returnLineId, tierDayType, lastTrain, { todayYmd: tierYmd })
    : null;
  const isNeutral = !!(tier && tier.tier === 'neutral');
  const chip = (tier && !isNeutral && FF) ? FF.chipInfo(tier) : null;

  const stById = {};
  for (const s of (stations || [])) if (s) stById[s.id] = s;
  const originName = (tier && tier.originId && stById[tier.originId] && window.displayStationName)
    ? window.displayStationName(stById[tier.originId]) : '';
  // Goal 4: the rider's OWN home stop — the GETTING BACK line leads with it
  // ("LAST TRAIN HOME TO {HOME}"), same display resolve as the home kicker so
  // the name matches the one the user picked.
  const homeName = (stById[homeId] && window.displayStationName)
    ? window.displayStationName(stById[homeId]) : '';

  const stepRowStyle = {
    display: 'flex', alignItems: 'stretch', gap: 12, minHeight: 44,
  };
  const railStyle = { position: 'relative', width: 20, flexShrink: 0 };
  const textStyle = {
    display: 'flex', alignItems: 'center',
    fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600,
    letterSpacing: '0.02em', textTransform: 'uppercase', color: lens.ink,
    lineHeight: 1.3, padding: '8px 0',
  };

  // Rail cell: a hairline connector (trimmed at the ends) + the step glyph.
  const rail = (idx, total, node) => (
    <div style={railStyle}>
      <div aria-hidden="true" style={{
        position: 'absolute', left: 9, width: 2, background: lens.hairline,
        top: idx === 0 ? '50%' : 0, bottom: idx === total - 1 ? '50%' : 0,
      }} />
      <div style={{ position: 'absolute', left: 4, top: '50%', transform: 'translateY(-50%)' }}>
        {node}
      </div>
    </div>
  );

  const totalRows = steps.length + 1; // + arrival
  const rideGlyph = (color) => (
    <span aria-hidden="true" style={{
      display: 'block', width: 10, height: 10, borderRadius: 2,
      background: color || lens.muted,
      boxShadow: `0 0 0 2px ${lens.panel}`,
    }} />
  );
  const transferGlyph = (
    <span aria-hidden="true" style={{
      display: 'block', width: 10, height: 10, borderRadius: 2,
      background: 'transparent', border: `2px solid ${lens.muted}`,
      boxSizing: 'border-box',
    }} />
  );
  const arrivalGlyph = (
    <span aria-hidden="true" style={{
      display: 'block', width: 14, textAlign: 'center', fontSize: 13,
      fontWeight: 800, color: lens.reach1, background: lens.panel,
    }}>{'→'}</span>
  );

  return (
    <div style={{ marginTop: 4 }}>
      {/* Step list — §5.1 */}
      <div role="list" aria-label="Route from home">
        {steps.map((step, i) => (
          <div role="listitem" key={i} style={stepRowStyle}>
            {rail(i, totalRows, step.kind === 'transfer' ? transferGlyph : rideGlyph(step.lineColor))}
            <div style={textStyle}>{FF ? FF.stepText(step) : ''}</div>
          </div>
        ))}
        {/* Arrival step — D4 */}
        <div role="listitem" style={stepRowStyle}>
          {rail(steps.length, totalRows, arrivalGlyph)}
          <div style={{ ...textStyle, color: lens.reach1 }}>
            {`YOU'RE THERE — ${String(arrivalLabel || '').toUpperCase()}`}
          </div>
        </div>
      </div>

      {/* GETTING BACK — §5.2 "always when home set". Renders for any return-line
          tier; 'neutral' (B7 — unparseable event start) shows the factual last-
          departure line in ink, no urgency color, no chip. */}
      {tier && (
        <div style={{
          marginTop: 16, paddingTop: 14, borderTop: `1px solid ${lens.hairline}`,
        }}>
          <div style={{
            fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 700,
            letterSpacing: '0.1em', textTransform: 'uppercase', color: lens.muted,
            marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          }}>
            {'GETTING BACK'}
            {tier.holiday && (
              <span style={{
                fontFamily: 'var(--font-body)', fontSize: 9, fontWeight: 700,
                letterSpacing: '0.08em', color: darkMode ? '#FFB778' : '#8E4E00',
                background: darkMode ? 'rgba(255,183,120,0.10)' : 'rgba(142,78,0,0.10)',
                border: '1px solid currentColor', padding: '2px 7px', borderRadius: 6,
              }}>HOLIDAY SCHED</span>
            )}
          </div>
          <div style={{
            fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600,
            letterSpacing: '0.02em', textTransform: 'uppercase',
            color: isNeutral ? lens.ink : flowsChipColors(chip.colorKey, darkMode).text, lineHeight: 1.4,
          }}>{FF ? FF.gettingBackText(tier, originName, homeName) : ''}</div>
          {/* First-station honesty line — F7 (fact / neutral: any real last-
              train row's clock is the run's FIRST-STATION departure). Goal 4:
              the main line leads with HOME, so this sub-line names the origin
              station the clock belongs to — a train leaving {origin} at that
              clock can't be misread as ARRIVING home at that clock.
              P1: this sub-line is now LOAD-BEARING, not decorative. Stripping
              the graded tier does not strip the implicature — a clock sitting
              under an event still whispers "and you can make it" by adjacency.
              The second sentence carries that weight: no schedule knows the
              show's END, and the subtraction is handed to the rider — named,
              not silently dropped. Copy is Rhea's (co-signed by Reza on the
              P1 PR): the hinge must stay on the show's end ("lets out") and
              no wording may quantify duration — "shows usually run ~2h" would
              smuggle the duration model back in through copy and is vetoed.
              First sentence is the shipped Goal-4 prose, unchanged. */}
          {(tier.tier === 'fact' || isNeutral) && (
            <p style={{
              fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 400,
              color: lens.muted, lineHeight: 1.5, margin: '6px 0 0',
            }}>
              That&rsquo;s when the last train leaves {originName ? `${originName}, the start of the line` : 'the start of the line'} &mdash; your stop catches it later, so shave a few minutes. No schedule knows when your show lets out. That math is yours.
            </p>
          )}
          {/* Holiday note — F8 */}
          {tier.holiday && (
            <p style={{
              fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 400,
              color: lens.muted, lineHeight: 1.5, margin: '6px 0 0',
            }}>
              {`Holiday schedule today — times may run different. Check ${tier.systemLabel} before you ride.`}
            </p>
          )}
        </div>
      )}

      {/* CTA — D5, relocated here from the feed row (§8) */}
      {ctaUrl && (
        <div style={{ marginTop: 16 }}>
          <a
            href={ctaUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="focus-double-ring"
            onMouseEnter={() => setCtaHov(true)}
            onMouseLeave={() => setCtaHov(false)}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              minHeight: 44, padding: '10px 18px',
              fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 800,
              letterSpacing: '0.1em', textTransform: 'uppercase',
              color: darkMode ? '#10110F' : '#fff',
              background: ctaHov ? (darkMode ? '#ff8352' : '#a63200') : lens.reach1,
              borderRadius: Math.min(radius, 10), textDecoration: 'none',
              transition: 'background 0.2s ease',
            }}
          >TAKE ME THERE {'→'}</a>
        </div>
      )}
    </div>
  );
}

/* ── Event card ── Goal 3: a real <button> that opens the shared Spotlight
 * detail popover (window.SearchDetailPopover) for this event. onOpen receives
 * (event, triggerRect) — the parent captures the rect for the FLIP morph and
 * owns the popover mount. The single route CTA still lives in FlowsRoutePanel. */
function FlowsDetailEventCard({ event, darkMode, radius, onOpen }) {
  const lens = darkMode ? DETAIL_LENS.dark : DETAIL_LENS.light;
  const catColor = DETAIL_CAT_COLORS[event.category] || lens.muted;
  const catInk = window.categoryTextColor ? window.categoryTextColor(catColor) : '#fff';
  const [hov, setHov] = React.useState(false);

  // aria: title + venue (spec §2) — the button also advertises the dialog it
  // opens via aria-haspopup so the label needs no trailing action verb.
  const aria = `${event.title}${event.venue ? `, ${event.venue}` : ''}`;
  const hoverBorder = darkMode ? 'rgba(255,255,255,0.18)' : 'rgba(26,23,20,0.24)';

  return (
    <button
      type="button"
      className="focus-double-ring"
      aria-label={aria}
      aria-haspopup="dialog"
      onClick={(e) => { if (onOpen) onOpen(event, e.currentTarget.getBoundingClientRect()); }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        ...glassStyle(darkMode, 'solid'),
        display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
        minHeight: 44,
        border: `1px solid ${hov ? hoverBorder : lens.hairline}`,
        borderRadius: Math.min(radius, 12),
        padding: '12px 14px',
        transform: hov ? 'translateY(-1px)' : 'translateY(0)',
        transition: 'border-color 0.2s ease, transform 0.2s ease',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
        <div style={{
          fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 700,
          color: lens.ink, lineHeight: 1.3, flex: 1,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }}>{event.title}</div>
        {event.category && (
          <span style={{
            fontFamily: 'var(--font-body)', fontSize: 9, fontWeight: 700,
            letterSpacing: '0.08em', textTransform: 'uppercase',
            color: catInk, background: catColor,
            padding: '3px 8px', borderRadius: 6, flexShrink: 0, whiteSpace: 'nowrap',
          }}>{event.category}</span>
        )}
      </div>
      <div style={{
        fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 500,
        color: lens.muted, lineHeight: 1.4,
      }}>
        {event.venue}{event.time ? ` · ${event.time}` : ''}{event.date ? ` · ${event.date}` : ''}
      </div>
    </button>
  );
}

function FlowsDetail({ station, activeStation, events, distance, routePrev, lines, lastTrain, headways, fares, stations, linkedEvent, linkedDayContext, focusSignal, darkMode, radius }) {
  const FF = window.FlowsFeedFormat;
  const lens = darkMode ? DETAIL_LENS.dark : DETAIL_LENS.light;
  const ramp = darkMode ? DETAIL_RAMP.dark : DETAIL_RAMP.light;

  // Deep-link desktop focus (§2.6): FlowsApp bumps focusSignal on a resolved
  // arrival → scroll the panel into view, then move focus to its h2 (hooks run
  // unconditionally, before the no-station early return — Rules of Hooks).
  const headingRef = React.useRef(null);
  React.useEffect(() => {
    if (!focusSignal || !headingRef.current) return;
    const el = headingRef.current;
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (el.scrollIntoView) el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
    el.focus({ preventScroll: true });
  }, [focusSignal]);

  // Goal 3 — station-detail cards: cap the list at 5 (SHOW MORE reveals the
  // rest) and open the shared Spotlight popover for a tapped event. Hooks run
  // unconditionally, before the no-station early return (Rules of Hooks).
  const CARD_CAP = 5;
  const [showAllEvents, setShowAllEvents] = React.useState(false);
  const [popoverEvent, setPopoverEvent] = React.useState(null);
  const [popoverRect, setPopoverRect] = React.useState(null);
  const stationId = station ? station.id : null;
  // A new station starts collapsed and drops any lingering popover.
  React.useEffect(() => {
    setShowAllEvents(false);
    setPopoverEvent(null);
    setPopoverRect(null);
  }, [stationId]);
  const openEventDetail = React.useCallback((ev, rect) => {
    setPopoverEvent(ev);
    setPopoverRect(rect || null);
  }, []);
  const closeEventDetail = React.useCallback(() => {
    setPopoverEvent(null);
    setPopoverRect(null);
  }, []);

  const getSystem = (st) => {
    if (!st || !st.system) return { label: '?', color: '#888' };
    const info = window.SYSTEM_INFO && window.SYSTEM_INFO[st.system];
    return info || { label: st.system, color: '#888' };
  };
  const displayName = (st) => (window.displayStationName ? window.displayStationName(st) : (st ? st.name : ''));

  /* No station selected */
  if (!station) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        minHeight: 240, padding: 32, textAlign: 'center',
      }}>
        <div>
          <p style={{
            fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 800,
            textTransform: 'uppercase', color: lens.muted, margin: '0 0 8px', lineHeight: 1.3,
          }}>TAP A STATION</p>
          <p style={{
            fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500,
            color: lens.muted, lineHeight: 1.6, margin: 0,
          }}>See the route home and what&rsquo;s popping near any stop.</p>
        </div>
      </div>
    );
  }

  const home = activeStation || null;
  const isHome = home && station.id === home.id;
  const sys = getSystem(station);
  const homeSet = !!home;

  // Representative event start for the station-scoped GETTING BACK: the soonest
  // parseable show at this station (§5.2 tier is start-dependent). §2.2 override:
  // a deep-linked event becomes THE representative (its start + its date's
  // dayType/holiday) and leads the station's event list.
  const linked = linkedEvent || null;
  let repStart = null;
  // #246 (C2's fifth call site): a timeUnresolved row carries two REAL
  // published clocks whose merge the pipeline refused — its rendered time is
  // one honest clock of two, so the GETTING BACK grade must not trust it.
  // Null start feeds lastTrainTier's EXISTING '!startKnown → neutral' branch
  // (Mav's stranding ruling: "a false you-have-a-train strands someone at
  // 1 AM"). Same guard shape as C2's four (FlowsEventFeed ×2, FlowsApp ×2).
  // The sort-only read in eventSortKey below stays unguarded by ruling —
  // ordering a list is not grading a return trip (Mav-verified).
  if (linked && FF) {
    repStart = linked.timeUnresolved === true ? null : FF.parseEventStartMinutes(linked.time);
  } else if (FF && events) {
    for (const ev of events) {
      const m = ev.timeUnresolved === true ? null : FF.parseEventStartMinutes(ev.time);
      if (m != null && (repStart === null || m < repStart)) repStart = m;
    }
  }
  const routeDayTypeOverride = (linked && linkedDayContext) ? linkedDayContext.dayType : undefined;
  const routeYmdOverride = (linked && linkedDayContext) ? linkedDayContext.ymd : undefined;
  // Soonest-first across ALL dates (spec §1: keep every date — do NOT filter to
  // tonight). Unparseable date sinks to the end; within a day an unparseable
  // time sinks last — the same null-last convention as the feed's chrono sort.
  const DAY_LAST = Number.MAX_SAFE_INTEGER;
  const MIN_LAST = 100000; // > any real 0–1439 minute-of-day
  const todayParts = FF ? FF.pacificToday() : null;
  const eventSortKey = (ev) => {
    const dp = FF ? FF.parseEventDateParts(ev && ev.date, todayParts) : null;
    const day = dp ? Date.UTC(dp.y, dp.m0, dp.d) : DAY_LAST;
    const sm = FF ? FF.parseEventStartMinutes(ev && ev.time) : null;
    return { day, min: (sm == null ? MIN_LAST : sm) };
  };
  let orderedEvents = (events || []).slice();
  if (FF) {
    orderedEvents = orderedEvents
      .map((ev) => ({ ev, k: eventSortKey(ev) }))
      .sort((a, b) => (a.k.day - b.k.day) || (a.k.min - b.k.min))
      .map((x) => x.ev);
  }
  // The deep-linked representative event (§2.2) always leads and stays visible.
  if (linked) {
    const same = (e) => (linked.id != null ? e.id === linked.id : e.slug === linked.slug);
    orderedEvents = [linked, ...orderedEvents.filter((e) => !same(e))];
  }
  const visibleEvents = showAllEvents ? orderedEvents : orderedEvents.slice(0, CARD_CAP);
  const hiddenCount = orderedEvents.length - visibleEvents.length;

  // D1 kicker — FROM HOME · ~t MIN (range-aware via first-line headway).
  // P1 headway date fix: the headway range must come from the SAME day the
  // GETTING BACK tier grades. A deep-linked event on another day
  // (routeDayTypeOverride) answers from the EVENT's dayType + its start-hour
  // band; if that can't be answered (no parseable start / no data), render NO
  // range — silence over a date-wrong number. Tonight (no override) keeps the
  // live current band, which is the tier's day by construction.
  let kicker = null;
  if (homeSet && !isHome && distance && FF) {
    const band = window.bandForMinutes ? window.bandForMinutes(distance.time) : null;
    const steps = window.buildRouteSteps ? window.buildRouteSteps(home.id, station.id, routePrev, stations || [], lines) : [];
    const firstRide = steps.find((s) => s.kind === 'ride');
    let h;
    if (firstRide) {
      if (routeDayTypeOverride) {
        const evBand = (repStart != null && window.bandForHour) ? window.bandForHour(Math.floor(repStart / 60)) : null;
        h = evBand ? FF.bandHeadway(firstRide.lineId, headways, evBand, routeDayTypeOverride) : undefined;
      } else {
        const now = window.currentBand ? window.currentBand() : { band: 'late', dayType: 'weekday' };
        h = FF.bandHeadway(firstRide.lineId, headways, now.band, now.dayType);
      }
    }
    const parts = FF.reachFigureParts(distance.time, h);
    if (parts) {
      // Adult Clipper fare for the SAME steps (lib/flows-fares.js) — null until
      // fares.json has loaded or when a leg can't be priced, and then the line
      // simply ends at the minutes. It renders in muted ink, not the band
      // color: a price is data, and data gets no verdict color (the 'fact'
      // chip rule above).
      const cents = (fares && window.FlowsFares) ? window.FlowsFares.tripFareCents(steps, fares, stations) : null;
      kicker = {
        text: `FROM HOME · ${parts.figure} ${parts.unit}`,
        color: band ? ramp[band] : lens.ink,
        fare: cents != null ? window.FlowsFares.formatFare(cents) : null,
      };
    }
  }

  const ctaUrl = `https://www.google.com/maps/dir/?api=1&destination=${station.lat},${station.lng}&travelmode=transit`;

  return (
    <div id="flows-detail" style={{ padding: '20px 0' }}>
      {/* Station / event header */}
      <div style={{ marginBottom: 16 }}>
        <h2 ref={headingRef} tabIndex={-1} style={{
          fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 800,
          textTransform: 'uppercase', color: isHome ? lens.reach1 : lens.ink,
          lineHeight: 1.15, margin: 0, outline: 'none',
        }}>
          {isHome ? `YOU — ${displayName(station)}` : displayName(station)}
        </h2>
        {kicker ? (
          <div style={{
            fontFamily: 'var(--font-display)', fontSize: 12, fontWeight: 800,
            letterSpacing: '0.08em', textTransform: 'uppercase',
            color: kicker.color, marginTop: 6,
          }}>
            {kicker.text}
            {kicker.fare && (
              <span title="Adult Clipper fare (511 SF Bay)" style={{ color: lens.muted }}>{` · ${kicker.fare}`}</span>
            )}
          </div>
        ) : (
          <div style={{
            fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 500,
            color: lens.muted, marginTop: 6, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          }}>
            <span>{sys.label}{station.city ? ` · ${station.city}` : ''}</span>
            <span style={{
              fontFamily: 'var(--font-body)', fontSize: 9, fontWeight: 700,
              letterSpacing: '0.08em', textTransform: 'uppercase',
              color: window.categoryTextColor ? window.categoryTextColor(sys.color) : '#fff',
              background: sys.color, padding: '3px 8px', borderRadius: 100,
            }}>{sys.label}</span>
          </div>
        )}
      </div>

      {/* Route from home + GETTING BACK — §5.1/§5.2 (when home is set) */}
      {homeSet && !isHome && distance && (
        <div style={{ marginBottom: 20 }}>
          <FlowsRoutePanel
            homeId={home.id}
            station={station}
            arrivalLabel={displayName(station)}
            ctaUrl={ctaUrl}
            eventStartMin={repStart}
            dayTypeOverride={routeDayTypeOverride}
            todayYmdOverride={routeYmdOverride}
            routePrev={routePrev}
            stations={stations}
            lines={lines}
            lastTrain={lastTrain}
            darkMode={darkMode}
            radius={radius}
          />
        </div>
      )}

      <div style={{ height: 1, background: lens.hairline, marginBottom: 20 }} />

      {/* Station's events (deep-linked event leads — §2.2). Capped at 5 soonest;
          SHOW MORE reveals the rest (one-way, matching the feed's LONG HAUL
          reveal). Each card opens the shared Spotlight popover (below). */}
      {orderedEvents && orderedEvents.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {visibleEvents.map((event, i) => (
            <FlowsDetailEventCard
              key={event.id || event.slug || i}
              event={event}
              darkMode={darkMode}
              radius={radius}
              onOpen={openEventDetail}
            />
          ))}
          {hiddenCount > 0 && (
            <button
              type="button"
              className="focus-double-ring"
              onClick={(e) => {
                // Goal 6 audit — reveal-focus continuity (matches the feed's
                // SHOW MORE): this button unmounts on activation; hand focus to
                // the first newly revealed card instead of dropping to <body>.
                const panel = e.currentTarget.closest('section, div');
                setShowAllEvents(true);
                requestAnimationFrame(() => {
                  const scope = panel && panel.isConnected ? panel : document;
                  const cards = scope.querySelectorAll('button[aria-haspopup="dialog"]');
                  if (cards[CARD_CAP]) cards[CARD_CAP].focus();
                });
              }}
              style={{
                display: 'block', width: '100%', minHeight: 44, cursor: 'pointer',
                fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 11,
                letterSpacing: '0.1em', textTransform: 'uppercase', color: lens.muted,
                background: 'transparent', border: `1px dashed ${lens.hairline}`,
                borderRadius: Math.min(radius, 12), padding: '12px 14px',
              }}
            >{`SHOW MORE (${hiddenCount})`}</button>
          )}
        </div>
      ) : (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', padding: '40px 24px', textAlign: 'center',
        }}>
          <p style={{
            fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 700,
            textTransform: 'uppercase', color: lens.muted, margin: '0 0 8px',
          }}>{`${displayName(station)} IS QUIET RIGHT NOW`}</p>
          <p style={{
            fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500,
            color: lens.muted, margin: 0,
          }}>Slide up or down the line &mdash; the Bay always delivers.</p>
        </div>
      )}

      {/* Standalone directions CTA for the no-route case (browse / no home) —
          the route panel owns the CTA when a home route exists; this keeps
          TAKE ME THERE reachable everywhere else (§8 "relocated, not removed"). */}
      {!isHome && !(homeSet && distance) && (
        <div style={{ marginTop: 20 }}>
          <a
            href={ctaUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="focus-double-ring"
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              minHeight: 44, padding: '10px 18px',
              fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 800,
              letterSpacing: '0.1em', textTransform: 'uppercase',
              color: darkMode ? '#10110F' : '#fff', background: lens.reach1,
              borderRadius: Math.min(radius, 10), textDecoration: 'none',
            }}
          >TAKE ME THERE {'→'}</a>
        </div>
      )}

      {/* Shared Spotlight detail — REUSES window.SearchDetailPopover (poster,
          category, MERCH + SHARE, tickets). It portals to <body>, owns ESC /
          scroll-lock / focus-return itself, and restores focus to the card that
          opened it. Guarded so a missing bundle degrades to a no-op, not a throw. */}
      {popoverEvent && typeof window.SearchDetailPopover === 'function' && (
        <window.SearchDetailPopover
          item={{ kind: 'event', event: popoverEvent }}
          darkMode={darkMode}
          triggerRect={popoverRect}
          onClose={closeEventDetail}
        />
      )}
    </div>
  );
}

FlowsRoutePanel.propTypes = {
  homeId: PropTypes.string,
  station: PropTypes.object,
  arrivalLabel: PropTypes.string,
  ctaUrl: PropTypes.string,
  eventStartMin: PropTypes.number,
  dayTypeOverride: PropTypes.string, // §2.2 deep-link — event's dayType for the tier
  todayYmdOverride: PropTypes.string, // §2.2 deep-link — event's ymd for the holiday check
  routePrev: PropTypes.object,
  stations: PropTypes.array,
  lines: PropTypes.object,
  lastTrain: PropTypes.object,
  darkMode: PropTypes.bool.isRequired,
  radius: PropTypes.number.isRequired,
};

FlowsDetailEventCard.propTypes = {
  event: PropTypes.object.isRequired,
  darkMode: PropTypes.bool.isRequired,
  radius: PropTypes.number.isRequired,
  onOpen: PropTypes.func, // (event, triggerRect) — open the shared Spotlight popover
};

FlowsDetail.propTypes = {
  station: PropTypes.object,
  activeStation: PropTypes.object,
  events: PropTypes.array,
  distance: PropTypes.shape({ stops: PropTypes.number, time: PropTypes.number }),
  routePrev: PropTypes.object,
  lines: PropTypes.object,
  lastTrain: PropTypes.object,
  headways: PropTypes.object,
  fares: PropTypes.object,            // data/fares.json (adult Clipper) — null until loaded; the kicker appends the trip price
  stations: PropTypes.array,
  linkedEvent: PropTypes.object,      // §2.2 deep-link representative event (this station)
  linkedDayContext: PropTypes.object, // { dayType, ymd } derived from the linked event's date
  focusSignal: PropTypes.number,      // §2.6 desktop — bump to focus the panel h2
  darkMode: PropTypes.bool.isRequired,
  radius: PropTypes.number.isRequired,
};

window.FlowsLastTrainChip = FlowsLastTrainChip;
window.FlowsRoutePanel = FlowsRoutePanel;
window.flowsChipColors = flowsChipColors;
window.FlowsDetail = FlowsDetail;
